import {
    Checkbox,
    type CheckboxProps,
    useMantineColorScheme,
} from '@mantine/core';
import { IconCheck, IconQuestionMark } from '@tabler/icons-react';
import type { ChangeEvent } from 'react';
import type { Except, Merge, SetRequired } from 'type-fest';
import { LabelNode } from '../getLabelNode';
import type {
    Controlled_InputWithLabelProps,
    ValueOrNullableValue,
} from './types';

export type BooleanOrNullableBoolean<N extends boolean> = ValueOrNullableValue<
    N,
    boolean
>;

export type Controlled_BooleanInputProps<N extends boolean> = Merge<
    Except<SetRequired<CheckboxProps, 'readOnly'>, 'checked' | 'indeterminate'>,
    Controlled_InputWithLabelProps<N, boolean>
>;
export const Controlled_BooleanInput = <N extends boolean>({
    nullable,
    value,
    onChange,
    label,
    labelInfo,
    readOnly,
    color,
    ...props
}: Controlled_BooleanInputProps<N>) => {
    const { colorScheme } = useMantineColorScheme();
    const handleChange =
        onChange ?
            (event: ChangeEvent<HTMLInputElement>) => {
                if (nullable) {
                    const newValue = (() => {
                        switch (value) {
                            case true: {
                                return null;
                            }
                            case null: {
                                return false;
                            }
                            case false: {
                                return true;
                            }
                            default: {
                                return value satisfies never;
                            }
                        }
                    })();
                    onChange(newValue as BooleanOrNullableBoolean<N>);
                } else {
                    onChange(event.currentTarget.checked);
                }
            }
        :   undefined;
    return (
        <Checkbox
            {...props}
            checked={value ?? false}
            indeterminate={value === null}
            readOnly={readOnly}
            onChange={readOnly ? undefined : handleChange}
            label={
                label && (
                    <LabelNode
                        label={label}
                        labelInfo={labelInfo}
                    />
                )
            }
            color={color ?? (readOnly ? 'dimgray' : undefined)}
            icon={({ indeterminate, ...iconProps }) =>
                indeterminate ?
                    <IconQuestionMark {...iconProps} />
                :   <IconCheck {...iconProps} />
            }
            variant={
                value === null || (readOnly && colorScheme === 'dark') ?
                    'outline'
                :   'filled'
            }
        />
    );
};
